import React from 'react';
import { Link } from 'react-router-dom';
import { FiMail, FiPhone, FiMapPin, FiClock, FiExternalLink } from 'react-icons/fi';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  // Helper function to get the correct path
  const getPath = (path) => {
    // Check if we're in preview mode
    const urlParams = new URLSearchParams(window.location.search);
    const isPreviewMode = urlParams.get('preview') === 'true';
    
    // If in preview mode, add the preview parameter
    if (isPreviewMode) {
      return `${path}?preview=true`;
    }
    
    return path;
  };
  
  return (
    <footer className="relative bg-neutral-950 text-white overflow-hidden">
      {/* Modern gradient background with subtle effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 via-neutral-950 to-primary-950/20"></div>
      
      {/* Sophisticated grain texture */}
      <div className="absolute inset-0 opacity-20 mix-blend-overlay">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJub2lzZSIgeD0iMCIgeT0iMCIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSI+PGZlVHVyYnVsZW5jZSBiYXNlRnJlcXVlbmN5PSIwLjgiIHN0aXRjaFRpbGVzPSJzdGl0Y2giIHR5cGU9ImZyYWN0YWxOb2lzZSIgbnVtT2N0YXZlcz0iMyIgc2VlZD0iMiIgcmVzdWx0PSJub2lzZSI+PC9mZVR1cmJ1bGVuY2U+PGZlQ29sb3JNYXRyaXggaW49Im5vaXNlIiB0eXBlPSJzYXR1cmF0ZSIgdmFsdWVzPSIwIj48L2ZlQ29sb3JNYXRyaXg+PC9maWx0ZXI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsdGVyPSJ1cmwoI25vaXNlKSIgb3BhY2l0eT0iMC40Ij48L3JlY3Q+PC9zdmc+')] bg-repeat opacity-40"></div>
      </div>
      
      {/* Subtle geometric pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ 
          backgroundImage: `linear-gradient(30deg, #4b5563 1px, transparent 1px), linear-gradient(-30deg, #4b5563 1px, transparent 1px)`,
          backgroundSize: '60px 60px' 
        }}></div>
      </div>
      
      {/* Modern accent lines */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary-500/60 to-transparent"></div>
      
      {/* Floating accent elements */}
      <div className="absolute top-16 right-20 w-32 h-32 bg-gradient-radial from-primary-600/10 to-transparent rounded-full blur-2xl"></div>
      <div className="absolute bottom-20 left-16 w-40 h-40 bg-gradient-radial from-secondary-600/8 to-transparent rounded-full blur-3xl"></div>
      
      {/* Main Footer Content */}
      <div className="relative z-10">
        {/* Hero Section - Brand & Description */}
        <div className="container mx-auto px-6 pt-20 pb-16">
          <div className="text-center mb-16">
            <Link to="/" className="inline-block mb-6 group">
              {/* Add CSS for logo icon swaying animation */}
              <style>
                {`
                  @keyframes logo-sway {
                    0% { transform: rotate(4deg); }
                    50% { transform: rotate(-4deg); }
                    100% { transform: rotate(4deg); }
                  }
                  
                  .logo-icon-sway {
                    animation: logo-sway 3s ease-in-out infinite;
                    transform-origin: center bottom;
                  }
                `}
              </style>
              <div className="flex items-center justify-center space-x-1 mb-2">
                {/* Icon part with swaying animation */}
                <img 
                  src="/images/logo-icon.svg" 
                  alt="InSocia Icon" 
                  className="h-16 w-auto transition-all duration-300 group-hover:scale-110 group-hover:drop-shadow-lg logo-icon-sway"
                />
                {/* Text part - static */}
                <img 
                  src="/images/logo-text.svg" 
                  alt="InSocia Text" 
                  className="h-16 w-auto transition-all duration-300 group-hover:drop-shadow-lg brightness-0 invert"
                />
              </div>
              <div className="flex justify-center">
                <div className="w-16 h-[2px] bg-gradient-to-r from-transparent via-primary-400 to-transparent group-hover:via-primary-300 transition-colors duration-300"></div>
              </div>
            </Link>
            
            <p className="text-neutral-300 max-w-3xl mx-auto text-lg font-light leading-relaxed mb-8">
              Transforming Society Through Evidence-Based Solutions - 
              Creating equitable societies through innovative policy approaches and rigorous research 
              that drives sustainable social development across communities.
            </p>
            
            {/* Enhanced Social Links */}
            <div className="flex justify-center space-x-4">
              <a href="#" aria-label="Facebook" className="group relative w-12 h-12 flex items-center justify-center rounded-xl bg-neutral-800/40 border border-neutral-700/50 text-neutral-300 hover:bg-primary-600/20 hover:border-primary-500/50 hover:text-primary-300 transition-all duration-300 backdrop-blur-sm">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd"></path>
                </svg>
              </a>
              <a href="#" aria-label="Twitter" className="group relative w-12 h-12 flex items-center justify-center rounded-xl bg-neutral-800/40 border border-neutral-700/50 text-neutral-300 hover:bg-primary-600/20 hover:border-primary-500/50 hover:text-primary-300 transition-all duration-300 backdrop-blur-sm">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"></path>
                </svg>
                <div className="absolute inset-0 rounded-xl bg-primary-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div>
              </a>
              <a href="#" aria-label="LinkedIn" className="group relative w-12 h-12 flex items-center justify-center rounded-xl bg-neutral-800/40 border border-neutral-700/50 text-neutral-300 hover:bg-primary-600/20 hover:border-primary-500/50 hover:text-primary-300 transition-all duration-300 backdrop-blur-sm">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd"></path>
                </svg>
                <div className="absolute inset-0 rounded-xl bg-primary-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div>
              </a>
              <a href="#" aria-label="Instagram" className="group relative w-12 h-12 flex items-center justify-center rounded-xl bg-neutral-800/40 border border-neutral-700/50 text-neutral-300 hover:bg-primary-600/20 hover:border-primary-500/50 hover:text-primary-300 transition-all duration-300 backdrop-blur-sm">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.80c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd"></path>
                </svg>
                <div className="absolute inset-0 rounded-xl bg-primary-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div>
              </a>
            </div>
          </div>
          
          {/* Modern Glass Card Layout */}
          <div className="relative">
            {/* Main content card with modern glass morphism */}
            <div className="backdrop-blur-xl bg-neutral-900/30 border border-neutral-700/30 rounded-3xl p-8 shadow-2xl">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                
                {/* Column 1: Navigation & Quick Links */}
                <div>
                  <h3 className="text-white font-heading font-semibold text-xl mb-6 relative">
                    <span className="bg-gradient-to-r from-primary-400 to-primary-300 bg-clip-text text-transparent">
                      Quick Links
                    </span>
                    <div className="absolute -bottom-2 left-0 w-8 h-[2px] bg-gradient-to-r from-primary-400 to-transparent"></div>
                  </h3>
                  <div className="grid grid-cols-3 gap-x-3 gap-y-2">
                    <Link to={getPath("/")} className="text-neutral-300 hover:text-primary-400 transition-all duration-300 text-sm hover:translate-x-1 flex items-center group">
                      <div className="w-1 h-1 bg-primary-400 rounded-full mr-2 group-hover:w-2 transition-all duration-300"></div>
                      Home
                    </Link>
                    <Link to={getPath("/about")} className="text-neutral-300 hover:text-primary-400 transition-all duration-300 text-sm hover:translate-x-1 flex items-center group">
                      <div className="w-1 h-1 bg-primary-400 rounded-full mr-2 group-hover:w-2 transition-all duration-300"></div>
                      About Us
                    </Link>
                    <Link to={getPath("/services")} className="text-neutral-300 hover:text-primary-400 transition-all duration-300 text-sm hover:translate-x-1 flex items-center group">
                      <div className="w-1 h-1 bg-primary-400 rounded-full mr-2 group-hover:w-2 transition-all duration-300"></div>
                      Services
                    </Link>
                    <Link to={getPath("/focus-areas")} className="text-neutral-300 hover:text-primary-400 transition-all duration-300 text-sm hover:translate-x-1 flex items-center group">
                      <div className="w-1 h-1 bg-primary-400 rounded-full mr-2 group-hover:w-2 transition-all duration-300"></div>
                      Focus Areas
                    </Link>
                    <Link to={getPath("/projects")} className="text-neutral-300 hover:text-primary-400 transition-all duration-300 text-sm hover:translate-x-1 flex items-center group">
                      <div className="w-1 h-1 bg-primary-400 rounded-full mr-2 group-hover:w-2 transition-all duration-300"></div>
                      Projects
                    </Link>
                    <Link to={getPath("/resources")} className="text-neutral-300 hover:text-primary-400 transition-all duration-300 text-sm hover:translate-x-1 flex items-center group">
                      <div className="w-1 h-1 bg-primary-400 rounded-full mr-2 group-hover:w-2 transition-all duration-300"></div>
                      Resources
                    </Link>
                    <Link to={getPath("/news")} className="text-neutral-300 hover:text-primary-400 transition-all duration-300 text-sm hover:translate-x-1 flex items-center group">
                      <div className="w-1 h-1 bg-primary-400 rounded-full mr-2 group-hover:w-2 transition-all duration-300"></div>
                      News & Insights
                    </Link>
                    <Link to={getPath("/careers")} className="text-neutral-300 hover:text-primary-400 transition-all duration-300 text-sm hover:translate-x-1 flex items-center group">
                      <div className="w-1 h-1 bg-primary-400 rounded-full mr-2 group-hover:w-2 transition-all duration-300"></div>
                      Careers
                    </Link>
                    <Link to={getPath("/contact")} className="text-neutral-300 hover:text-primary-400 transition-all duration-300 text-sm hover:translate-x-1 flex items-center group">
                      <div className="w-1 h-1 bg-primary-400 rounded-full mr-2 group-hover:w-2 transition-all duration-300"></div>
                      Contact
                    </Link>
                  </div>
                </div>
                
                {/* Column 2: Resources */}
                <div>
                  <h3 className="text-white font-heading font-semibold text-xl mb-6 relative">
                    <span className="bg-gradient-to-r from-secondary-400 to-secondary-300 bg-clip-text text-transparent">
                      Resources
                    </span>
                    <div className="absolute -bottom-2 left-0 w-8 h-[2px] bg-gradient-to-r from-secondary-500 to-transparent"></div>
                  </h3>
                                    <div className="space-y-2">
                    <Link to={getPath("/resources")} className="text-neutral-300 hover:text-secondary-400 transition-all duration-300 text-sm hover:translate-x-1 flex items-center group">
                      <div className="w-1 h-1 bg-secondary-500 rounded-full mr-2 group-hover:w-2 transition-all duration-300"></div>
                    Resources Overview
                  </Link>
                    <Link to={getPath("/resources#knowledge-hub")} className="text-neutral-300 hover:text-secondary-400 transition-all duration-300 text-sm hover:translate-x-1 flex items-center group">
                      <div className="w-1 h-1 bg-secondary-500 rounded-full mr-2 group-hover:w-2 transition-all duration-300"></div>
                    Knowledge Hub
                  </Link>
                    <Link to={getPath("/resources#data-repository")} className="text-neutral-300 hover:text-secondary-400 transition-all duration-300 text-sm hover:translate-x-1 flex items-center group">
                      <div className="w-1 h-1 bg-secondary-500 rounded-full mr-2 group-hover:w-2 transition-all duration-300"></div>
                      Data Repository
                    </Link>
                    <Link to={getPath("/resources#methodology-center")} className="text-neutral-300 hover:text-secondary-400 transition-all duration-300 text-sm hover:translate-x-1 flex items-center group">
                      <div className="w-1 h-1 bg-secondary-500 rounded-full mr-2 group-hover:w-2 transition-all duration-300"></div>
                      Methodology Center
                    </Link>
                    <Link to={getPath("/resources#policy-lab")} className="text-neutral-300 hover:text-secondary-400 transition-all duration-300 text-sm hover:translate-x-1 flex items-center group">
                      <div className="w-1 h-1 bg-secondary-500 rounded-full mr-2 group-hover:w-2 transition-all duration-300"></div>
                      Policy Innovation Lab
                    </Link>
                    <Link to={getPath("/resources#training")} className="text-neutral-300 hover:text-secondary-400 transition-all duration-300 text-sm hover:translate-x-1 flex items-center group">
                      <div className="w-1 h-1 bg-secondary-500 rounded-full mr-2 group-hover:w-2 transition-all duration-300"></div>
                      Training Resources
                    </Link>
                  </div>
                </div>
                
                {/* Column 3: Contact Information */}
                <div>
                  <h3 className="text-white font-heading font-semibold text-xl mb-6 relative">
                    <span className="bg-gradient-to-r from-primary-400 to-primary-300 bg-clip-text text-transparent">
                      Get in Touch
                    </span>
                    <div className="absolute -bottom-2 left-0 w-8 h-[2px] bg-gradient-to-r from-primary-500 to-transparent"></div>
                  </h3>
                  <div className="space-y-4">
                    <a href="mailto:connect@insocia.in" className="group flex items-start space-x-3 p-3 rounded-xl bg-neutral-800/20 hover:bg-neutral-800/40 border border-neutral-700/20 hover:border-neutral-600/40 transition-all duration-300">
                      <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-gradient-to-br from-primary-500/20 to-primary-600/20 flex items-center justify-center group-hover:from-primary-500/30 group-hover:to-primary-600/30 transition-all duration-300">
                        <FiMail className="w-4 h-4 text-primary-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">Email Us</p>
                        <p className="text-neutral-300 text-sm group-hover:text-primary-400 transition-colors duration-300">connect@insocia.in</p>
                      </div>
                    </a>
                    
                    <a href="tel:+917012345678" className="group flex items-start space-x-3 p-3 rounded-xl bg-neutral-800/20 hover:bg-neutral-800/40 border border-neutral-700/20 hover:border-neutral-600/40 transition-all duration-300">
                      <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-gradient-to-br from-primary-500/20 to-primary-600/20 flex items-center justify-center group-hover:from-primary-500/30 group-hover:to-primary-600/30 transition-all duration-300">
                        <FiPhone className="w-4 h-4 text-primary-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">Call Us</p>
                        <p className="text-neutral-300 text-sm group-hover:text-primary-400 transition-colors duration-300">+91 701 234 5678</p>
                      </div>
                    </a>
                    
                    <div className="group flex items-start space-x-3 p-3 rounded-xl bg-neutral-800/20 border border-neutral-700/20">
                      <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-gradient-to-br from-primary-500/20 to-primary-600/20 flex items-center justify-center">
                        <FiMapPin className="w-4 h-4 text-primary-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">Our Locations</p>
                        <p className="text-neutral-300 text-sm">AECS Layout, Sanjayanagara, Bengaluru, Karnataka 560094</p>
                        <p className="text-neutral-300 text-sm">Kozhikode, Kerala - 673573</p>
                      </div>
                    </div>
                    
                    <div className="group flex items-start space-x-3 p-3 rounded-xl bg-neutral-800/20 border border-neutral-700/20">
                      <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-gradient-to-br from-primary-500/20 to-primary-600/20 flex items-center justify-center">
                        <FiClock className="w-4 h-4 text-primary-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">Working Hours</p>
                        <p className="text-neutral-300 text-sm">Monday - Friday, 9AM - 6PM</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Legal Links Section */}
              <div className="mt-10 pt-8 border-t border-neutral-700/30">
                <div className="flex flex-wrap justify-center items-center gap-6">
                  <Link to={getPath("/privacy-policy")} className="text-neutral-400 hover:text-primary-400 transition-colors text-sm relative group">
                    Privacy Policy
                    <div className="absolute -bottom-1 left-0 w-0 h-[1px] bg-primary-400 group-hover:w-full transition-all duration-300"></div>
                  </Link>
                  <div className="w-1 h-1 bg-neutral-600 rounded-full"></div>
                  <Link to={getPath("/terms-of-use")} className="text-neutral-400 hover:text-primary-400 transition-colors text-sm relative group">
                    Terms of Use
                    <div className="absolute -bottom-1 left-0 w-0 h-[1px] bg-primary-400 group-hover:w-full transition-all duration-300"></div>
                  </Link>
                  <div className="w-1 h-1 bg-neutral-600 rounded-full"></div>
                  <Link to={getPath("/accessibility")} className="text-neutral-400 hover:text-primary-400 transition-colors text-sm relative group">
                    Accessibility
                    <div className="absolute -bottom-1 left-0 w-0 h-[1px] bg-primary-400 group-hover:w-full transition-all duration-300"></div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom Copyright Bar */}
        <div className="relative">
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-neutral-700/50 to-transparent"></div>
          <div className="bg-neutral-950/60 backdrop-blur-sm py-6">
            <div className="container mx-auto px-6">
              <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                <p className="text-neutral-500 text-sm">
                  © {currentYear} Insocia Consultancy. All rights reserved.
                </p>
                <p className="text-neutral-500 text-sm flex items-center space-x-2">
                  <span>Designed & Developed by</span>
                  <a 
                    href="https://edot.studio" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-primary-400 hover:text-primary-300 transition-colors font-medium inline-flex items-center space-x-1 group"
                  >
                    <span>edot.studio</span>
                    <FiExternalLink className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 